import type { ImageAnnotatorImage } from "@msuite/utils/types";

/**
 * The overlay inherits its typeface from the page, which a serialised SVG no longer sits in.
 * A generic stack keeps the exported labels close to the rendered ones without a font fetch —
 * an external font could not load from a data URL anyway.
 */
const FONT_STACK = "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const DEFAULT_QUALITY = 0.92;

/** Props Interface */
interface ExportAnnotatedImageOptions {
	src: string;
	svg: SVGSVGElement;
	image: ImageAnnotatorImage;
	quality?: number;
}

/**
 * Swaps a file name's extension for `.jpg`, so a downloaded export is named after the file it
 * was made from rather than carrying a misleading `.png` or `.heic` tail.
 *
 * @param fileName - The source file's name, which may be empty or carry no extension.
 * @returns The export's file name, always ending in `.jpg`.
 */
export function getAnnotatedImageFileName(fileName: string): string {
	const fallback = "annotated-image.jpg";
	const trimmed = fileName.trim();
	if (!trimmed) return fallback;
	const base = trimmed.replace(/\.[^./\\]+$/, "");
	if (!base) return fallback;
	return `${base}.jpg`;
}

function decodeImage(src: string, crossOrigin?: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const element = new Image();
		if (crossOrigin) element.crossOrigin = crossOrigin;
		element.onload = () => resolve(element);
		element.onerror = () => reject(new Error("Image failed to decode"));
		element.src = src;
	});
}

/**
 * Loads the source image in a form the canvas can read back from. A plain `<img src>` reuses the
 * browser's non-CORS cache entry, which taints the canvas and makes `toBlob` throw — so the bytes
 * are fetched first and handed over as a blob. The `crossOrigin` retry covers hosts that refuse
 * the fetch but answer an image request.
 */
async function loadSourceImage(src: string): Promise<HTMLImageElement> {
	try {
		const response = await fetch(src, { mode: "cors", credentials: "omit" });
		if (!response.ok) throw new Error(`Source image request failed: ${response.status}`);
		const objectUrl = URL.createObjectURL(await response.blob());
		try {
			return await decodeImage(objectUrl);
		} finally {
			URL.revokeObjectURL(objectUrl);
		}
	} catch {
		return await decodeImage(src, "anonymous");
	}
}

/**
 * Serialises the live overlay into a standalone SVG data URL sized to the source image. The
 * rendered overlay takes its size from CSS and its typeface from the page, and neither survives
 * serialisation, so both are written onto the clone as attributes. The `viewBox` already spans
 * the image's own coordinates, which is what makes the export land at full source resolution.
 */
function serializeOverlay(svg: SVGSVGElement, image: ImageAnnotatorImage): string {
	const clone = svg.cloneNode(true) as SVGSVGElement;
	clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
	clone.setAttribute("width", String(image.width));
	clone.setAttribute("height", String(image.height));
	clone.setAttribute("viewBox", `0 0 ${image.width} ${image.height}`);
	clone.setAttribute("font-family", FONT_STACK);
	const markup = new XMLSerializer().serializeToString(clone);
	return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
	return new Promise((resolve, reject) => {
		canvas.toBlob(
			(blob) => (blob ? resolve(blob) : reject(new Error("Canvas export produced no data"))),
			"image/jpeg",
			quality,
		);
	});
}

/**
 * Flattens the annotation overlay onto the source image and returns the result as a JPEG blob,
 * at the image's own pixel dimensions rather than the size it happens to be displayed at.
 *
 * @param options - The image source, the live overlay element, the source image's dimensions,
 * and an optional JPEG quality between 0 and 1.
 * @returns A JPEG blob of the image with its annotations drawn in.
 */
export async function exportAnnotatedImage({
	src,
	svg,
	image,
	quality = DEFAULT_QUALITY,
}: ExportAnnotatedImageOptions): Promise<Blob> {
	const [source, overlay] = await Promise.all([
		loadSourceImage(src),
		decodeImage(serializeOverlay(svg, image)),
	]);

	/**
	 * The canvas takes the decoded file's own dimensions rather than the stored ones, so a stale
	 * `width` on the record cannot quietly downsample the export. Both layers are then stretched
	 * to that one box, which is exactly what the screen does — so they stay in register even when
	 * the two disagree.
	 */
	const canvas = document.createElement("canvas");
	canvas.width = source.naturalWidth || image.width;
	canvas.height = source.naturalHeight || image.height;

	const context = canvas.getContext("2d");
	if (!context) throw new Error("Canvas 2D context unavailable");

	/** JPEG carries no alpha, so a transparent source would otherwise composite onto black. */
	context.fillStyle = "#ffffff";
	context.fillRect(0, 0, canvas.width, canvas.height);
	context.drawImage(source, 0, 0, canvas.width, canvas.height);
	context.drawImage(overlay, 0, 0, canvas.width, canvas.height);

	return await canvasToBlob(canvas, quality);
}
